'use client';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useTransition } from '@react-spring/web';
import { useInterval } from './useInterval';
import { useInView } from './useInView';
import { usePage } from './usePage';

export interface IUseCarousel {
  value: number;
  duration: number;
  total: number;
  stopAuto?: boolean;
  disabled?: boolean;
}

const useIsInitailRender = () => {
  const isInitialRender = useRef(true);

  useEffect(() => {
    isInitialRender.current = false;
  }, []);

  return isInitialRender.current;
};

export const useSpringCarousel = ({
  value = 0,
  duration = 0,
  total = 0,
  stopAuto = false,
  disabled = false,
}: IUseCarousel) => {
  const isInitialRender = useIsInitailRender();
  const [inViewRef, isInView] = useInView({ amount: 0.8 });
  const [isStop, setIsStop] = useState(stopAuto);
  const { current, onChange, onNext, onPrev, onCycleChange, pageDirection } =
    usePage({ page: value, pageSize: 1, total, direction: 'right' });

  const onStart = useCallback(() => {
    setIsStop(false);
  }, []);

  const onStop = useCallback(() => {
    setIsStop(true);
  }, []);
  const [transitions, api] = useTransition(current, () => ({
    from: {
      opacity: 0,
      transform: `translateX(${pageDirection === 'right' ? 100 : -100}%)`,
    },
    enter: { opacity: 1, transform: 'translateX(0%)' },
    leave: {
      opacity: 0,
      transform: `translateX(${pageDirection === 'right' ? -100 : 100}%)`,
    },
    loop: true,
    config: {
      duration: 500,
    },
    immediate: isInitialRender,
  }));

  useEffect(() => {
    setIsStop(stopAuto);
  }, [stopAuto]);

  useEffect(() => {
    // 用户仍可能在轮播未达到自动播放可视阈值时手动切换。
    // 可视状态只控制自动播放，不能阻止当前卡片的过渡动画。
    api.start();
  }, [current]);

  useInterval(
    {
      autoStart: isInView && !isStop,
      duration,
      callback: () => {
        if (!disabled) {
          onCycleChange();
        }
      },
    },
    [current, isStop]
  );

  return {
    transitions,
    onPrev: () => onCycleChange('left'),
    onDot: onChange,
    onNext: () => onCycleChange('right'),
    active: current,
    inViewRef,
    onStop,
    onStart,
    isStop,
    onCycleChange,
  };
};
